import { useEffect, useState } from "react";

import {
	getResponsiveBreakpoint,
	type TResponsiveBreakpoint,
} from "./breakpoints";

type TResponsivePointer = "coarse" | "fine";

export type TResponsiveState = {
	width: number;
	height: number;
	breakpoint: TResponsiveBreakpoint;
	isMobile: boolean;
	isTablet: boolean;
	isLaptop: boolean;
	isDesktop: boolean;
	isWide: boolean;
	pointer: TResponsivePointer;
};

const fallbackState: TResponsiveState = {
	width: 1280,
	height: 720,
	breakpoint: "desktop",
	isMobile: false,
	isTablet: false,
	isLaptop: false,
	isDesktop: true,
	isWide: false,
	pointer: "fine",
};

const getResponsiveState = (): TResponsiveState => {
	if (typeof window === "undefined") {
		return fallbackState;
	}

	const width = window.innerWidth;
	const height = window.innerHeight;
	const breakpoint = getResponsiveBreakpoint(width);
	const pointer = window.matchMedia(
		"(hover: none), (pointer: coarse)"
	).matches
		? "coarse"
		: "fine";

	return {
		width,
		height,
		breakpoint,
		isMobile: breakpoint === "mobile",
		isTablet: breakpoint === "tablet",
		isLaptop: breakpoint === "laptop",
		isDesktop: breakpoint === "desktop",
		isWide: breakpoint === "wide",
		pointer,
	};
};

export const useResponsive = (): TResponsiveState => {
	const [state, setState] =
		useState<TResponsiveState>(getResponsiveState);

	useEffect(() => {
		const updateState = () => {
			setState(getResponsiveState());
		};

		const pointerMedia = window.matchMedia(
			"(hover: none), (pointer: coarse)"
		);

		window.addEventListener("resize", updateState);
		window.addEventListener("orientationchange", updateState);
		pointerMedia.addEventListener("change", updateState);

		return () => {
			window.removeEventListener("resize", updateState);
			window.removeEventListener(
				"orientationchange",
				updateState
			);
			pointerMedia.removeEventListener(
				"change",
				updateState
			);
		};
	}, []);

	return state;
};
